/**
 * Row & Aisle Generator Domain Logic (Ticket 03).
 * Follows ASHRAE TC 9.9 standards for Data Center Hall Layouts.
 */

import { operatorPositionToIndex } from './rackName.logic.ts'
import { applyMask, buildMaskVars } from './naming.ts'
import { formatNamingProfile, resolveNamingProfileVersion, type NamingProfileCatalog, type NamingProfileAssignments } from './namingProfile.logic.ts'

export const PPM = 100 // 100 pixels per meter

export interface RowGeneratorOptions {
  rowName: string
  labelIndex: number
  startX_m: number
  startY_m: number
  rackCount: number
  rackWidth_m?: number
  rackDepth_m?: number
  rackHeight_m?: number
  orientation?: number // 0 | 90 | 180 | 270
  direction?: 'horizontal' | 'vertical'
  namingPattern?: string
  color?: string
  tenant?: string
  rackType?: string
  sequenceDirection?: 'forward' | 'reverse'
  module?: string
  hall?: string
  moduleId?: string
  namingProfiles?: NamingProfileCatalog
  namingAssignments?: NamingProfileAssignments
}

/**
 * Derives a clean, standard DCIM paired row name from the primary row name.
 * e.g. "Ряд A" -> "Ряд B", "Row-A" -> "Row-B", "Ряд 1" -> "Ряд 2", "Row 01" -> "Row 02"
 */
export function derivePairedRowName(primaryRowName: string): string {
  const trimmed = primaryRowName.trim()
  if (!trimmed) return 'Ряд B'

  // Trailing single letter with optional separator, e.g. "Ряд A", "Row-A", "A"
  const letterMatch = trimmed.match(/^(.*?[-_ ]?)([A-Za-zА-Яа-я])$/)
  if (letterMatch) {
    const prefix = letterMatch[1]
    const char = letterMatch[2]
    const code = char.charCodeAt(0)
    // English uppercase A..Y -> B..Z
    if (code >= 65 && code < 90) return `${prefix}${String.fromCharCode(code + 1)}`
    // English lowercase a..y -> b..z
    if (code >= 97 && code < 122) return `${prefix}${String.fromCharCode(code + 1)}`
    // Russian uppercase А..Я (1040..1071)
    if (code >= 1040 && code < 1071) return `${prefix}${String.fromCharCode(code + 1)}`
    // Russian lowercase а..я (1072..1103)
    if (code >= 1072 && code < 1103) return `${prefix}${String.fromCharCode(code + 1)}`
  }

  // Trailing integer with optional separator, e.g. "Ряд 1", "Row-01", "1"
  const numMatch = trimmed.match(/^(.*?[-_ ]?)(\d+)$/)
  if (numMatch) {
    const prefix = numMatch[1]
    const numStr = numMatch[2]
    const nextNum = parseInt(numStr, 10) + 1
    return `${prefix}${String(nextNum).padStart(numStr.length, '0')}`
  }

  return `${trimmed}-B`
}

/**
 * Suggests next sequential row index and name based on existing rows and racks.
 */
export function suggestNextRow(
  existingRows: Record<string, { labelIndex?: number; name?: string }>,
  existingRacks?: Record<string, { rowIndex?: number; row_index?: number }>
): {
  labelIndex: number
  suggestedName: string
} {
  const rowIndices = Object.values(existingRows).map(r => r.labelIndex || 1)
  if (existingRacks) {
    Object.values(existingRacks).forEach(rk => {
      const idx = rk.rowIndex ?? rk.row_index
      if (typeof idx === 'number' && idx >= 0 && idx < 10000) {
        rowIndices.push(idx + 1)
      }
    })
  }

  if (!rowIndices.length) {
    return { labelIndex: 1, suggestedName: 'Ряд A' }
  }
  const maxLabelIndex = Math.max(...rowIndices, 0)
  const nextIndex = maxLabelIndex + 1
  let letter = ''
  let n = nextIndex - 1
  while (n >= 0) {
    letter = String.fromCharCode(65 + (n % 26)) + letter
    n = Math.floor(n / 26) - 1
  }
  return { labelIndex: nextIndex, suggestedName: `Ряд ${letter}` }
}

export interface GeneratedRowResult {
  row: {
    id: string
    name: string
    labelIndex: number
    orientation: number
    color: string
    axis: 'horizontal' | 'vertical'
    head: { xM: number; yM: number }
    tail: { xM: number; yM: number }
    facing: number
    sequenceDirection: 'forward' | 'reverse'
    positions: Array<{ id: string; position: number }>
  }
  racks: Array<{
    id: string
    x: number
    y: number
    width: number
    height: number
    rotation: number
    orientation: number
    label: string
    rackType: string
    uCount: number
    isFacility: boolean
    isBlocked: boolean
    widthM: number
    depthM: number
    heightM: number
    tenant: string | null
    rowId: string
    rackPosInRow: number
    isDirty: boolean
    rowIndex: number
    rackIndex: number
  }>
}

export interface PairedRowOptions extends RowGeneratorOptions {
  aisleWidth_m: number
  aisleType?: 'auto' | 'cold' | 'hot' | 'containment'
  isContained?: boolean
}

export interface GeneratedAisleZone {
  id: string
  x: number
  y: number
  w: number
  h: number
  aisleType: 'cold' | 'hot' | 'containment'
  label: string
  isDirty: boolean
  isContained?: boolean
}

export interface PairedRowResult {
  primaryRow: GeneratedRowResult['row']
  primaryRacks: GeneratedRowResult['racks']
  pairedRow: GeneratedRowResult['row']
  pairedRacks: GeneratedRowResult['racks']
  aisleZone: GeneratedAisleZone
}

function genId(): string {
  return Math.random().toString(36).substring(2, 10)
}

export function formatRackIndex(pattern: string, rowName: string, index1Based: number): string {
  if (pattern.includes('{idx:02d}')) {
    const formattedIdx = String(index1Based).padStart(2, '0')
    return pattern.replace('{row}', rowName).replace('{idx:02d}', formattedIdx)
  }
  if (pattern.includes('{idx}')) {
    return pattern.replace('{row}', rowName).replace('{idx}', String(index1Based))
  }
  return `${rowName}-${String(index1Based).padStart(2, '0')}`
}

/**
 * Generates a row of N racks aligned with metric snapping (default 0.6m rack width).
 */
export function generateRowRacks(options: RowGeneratorOptions): GeneratedRowResult {
  const rowId = genId()
  const {
    rowName,
    labelIndex,
    startX_m,
    startY_m,
    rackCount,
    rackWidth_m = 0.6,
    rackDepth_m = 1.0,
    rackHeight_m = 2.0,
    orientation = 0,
    direction = 'horizontal',
    namingPattern = '{row}-{idx:02d}',
    color = '#3b82f6',
    tenant = null,
    rackType = 'server_rack',
    sequenceDirection = 'forward',
    module = '1',
    hall = 'A',
  } = options

  const axis = direction === 'horizontal' ? 'horizontal' : 'vertical'

  if (!Number.isInteger(rackCount) || rackCount < 1 || rackCount > 1000 || !Number.isInteger(labelIndex) || labelIndex < 1) throw new Error('Invalid row size or index')
  if (![startX_m, startY_m, rackWidth_m, rackDepth_m].every(Number.isFinite) || rackWidth_m <= 0 || rackDepth_m <= 0) throw new Error('Invalid row geometry')
  const row: GeneratedRowResult['row'] = {
    id: rowId,
    name: rowName,
    labelIndex,
    orientation,
    color,
    axis,
    head: { xM: startX_m, yM: startY_m },
    tail: { xM: startX_m, yM: startY_m },
    facing: orientation,
    sequenceDirection,
    positions: [],
  }

  const racks: GeneratedRowResult['racks'] = []

  for (let i = 0; i < rackCount; i++) {
    const posInRow = i + 1 // 1-based position for operators
    const distance = (sequenceDirection === 'reverse' ? rackCount - 1 - i : i) * rackWidth_m
    const x_m = direction === 'horizontal' ? startX_m + distance : startX_m
    const y_m = direction === 'vertical' ? startY_m + distance : startY_m

    const x_px = Math.round(x_m * PPM)
    const y_px = Math.round(y_m * PPM)
    const w_px = Math.round(rackWidth_m * PPM)
    const h_px = Math.round(rackDepth_m * PPM)

    racks.push({
      id: genId(),
      x: x_px,
      y: y_px,
      width: w_px,
      height: h_px,
      rotation: orientation,
      orientation,
      label: options.namingProfiles && options.namingAssignments
        ? formatNamingProfile(resolveNamingProfileVersion(options.namingProfiles, options.namingAssignments, { moduleId: options.moduleId }).profile, { module: Number(module), row: labelIndex, rack: posInRow })
        : namingPattern.includes('{rack')
        ? applyMask(namingPattern, buildMaskVars({ module, hall, row: labelIndex, rack: posInRow }))
        : formatRackIndex(namingPattern, rowName, posInRow),
      rackType,
      uCount: 42,
      isFacility: false,
      isBlocked: false,
      widthM: rackWidth_m,
      depthM: rackDepth_m,
      heightM: rackHeight_m,
      tenant,
      rowId,
      rackPosInRow: posInRow,
      isDirty: true,
      rowIndex: operatorPositionToIndex(labelIndex),
      rackIndex: operatorPositionToIndex(posInRow),
    })
    row.positions.push({ id: `${rowId}-position-${posInRow}`, position: posInRow })
  }

  const endDistance = (rackCount - 1) * rackWidth_m
  const startPoint = { xM: startX_m, yM: startY_m }
  const endPoint = {
    xM: direction === 'horizontal' ? startX_m + endDistance : startX_m,
    yM: direction === 'vertical' ? startY_m + endDistance : startY_m,
  }
  row.head = sequenceDirection === 'forward' ? startPoint : endPoint
  row.tail = sequenceDirection === 'forward' ? endPoint : startPoint

  return { row, racks }
}

/**
 * Detects whether an aisle between two rows is cold or hot according to ASHRAE front-to-front / back-to-back layout.
 * Unsupported relationships are invalid input; never silently default them to cold.
 */
export function detectAisleType(
  row1Orientation: number,
  row2Orientation: number,
  relativePos: 'below' | 'above' | 'right' | 'left' = 'below'
): 'cold' | 'hot' {
  if (relativePos === 'below') {
    // Row 1 is above (smaller Y), Row 2 is below (larger Y)
    // Orientation 0: front faces South (down, towards Row 2)
    // Orientation 180: front faces North (up, towards Row 1)
    if (row1Orientation === 0 && row2Orientation === 180) {
      return 'cold' // Front-to-Front
    }
    if (row1Orientation === 180 && row2Orientation === 0) {
      return 'hot' // Back-to-Back
    }
  } else if (relativePos === 'above') {
    if (row1Orientation === 180 && row2Orientation === 0) {
      return 'cold'
    }
    if (row1Orientation === 0 && row2Orientation === 180) {
      return 'hot'
    }
  } else if (relativePos === 'right') {
    // Row 1 is left, Row 2 is right
    // Orientation 270: front faces East (right, towards Row 2)
    // Orientation 90: front faces West (left, towards Row 1)
    if (row1Orientation === 270 && row2Orientation === 90) {
      return 'cold'
    }
    if (row1Orientation === 90 && row2Orientation === 270) {
      return 'hot'
    }
  }

  throw new Error(`Cannot determine aisle type for ${relativePos}: row facings ${row1Orientation}/${row2Orientation}`)
}

/**
 * Generates a paired row with matching corridor and orientation inversion.
 */
export function generatePairedRow(options: PairedRowOptions): PairedRowResult {
  const primaryResult = generateRowRacks(options)
  const { primaryRow, primaryRacks } = { primaryRow: primaryResult.row, primaryRacks: primaryResult.racks }

  const direction = options.direction ?? 'horizontal'
  const rackDepth_m = options.rackDepth_m ?? 1.0
  const aisleWidth_m = options.aisleWidth_m
  const isContained = Boolean(options.isContained)

  // Invert orientation for paired row
  const primaryOrient = options.orientation ?? 0
  const pairedOrient = (primaryOrient + 180) % 360

  // Calculate position of paired row
  let pairedStartX_m = options.startX_m
  let pairedStartY_m = options.startY_m

  if (direction === 'horizontal') {
    // Primary row is at startY_m, depth is rackDepth_m, corridor is aisleWidth_m
    pairedStartY_m = options.startY_m + rackDepth_m + aisleWidth_m
  } else {
    pairedStartX_m = options.startX_m + rackDepth_m + aisleWidth_m
  }

  const pairedRowName = derivePairedRowName(options.rowName)
  const pairedResult = generateRowRacks({
    ...options,
    rowName: pairedRowName,
    labelIndex: options.labelIndex + 1,
    startX_m: pairedStartX_m,
    startY_m: pairedStartY_m,
    orientation: pairedOrient,
  })

  // Detect corridor type
  const detectedAisle = detectAisleType(primaryOrient, pairedOrient, direction === 'horizontal' ? 'below' : 'right')
  const finalAisleType = isContained ? 'containment' : (options.aisleType === 'auto' || !options.aisleType ? detectedAisle : options.aisleType)

  // Compute AisleZone bounds
  let zoneX_px: number
  let zoneY_px: number
  let zoneW_px: number
  let zoneH_px: number

  const rackCount = options.rackCount
  const rackWidth_m = options.rackWidth_m ?? 0.6
  const rowLength_px = Math.round(rackCount * rackWidth_m * PPM)
  const aisleWidth_px = Math.round(aisleWidth_m * PPM)

  if (direction === 'horizontal') {
    zoneX_px = Math.round(options.startX_m * PPM)
    zoneY_px = Math.round((options.startY_m + rackDepth_m) * PPM)
    zoneW_px = rowLength_px
    zoneH_px = aisleWidth_px
  } else {
    // For vertical rows (rotated 90/270°), bounding box expands horizontally by (rackDepth_m - rackWidth_m)/2
    // and contracts vertically by the same amount.
    const delta_m = (rackDepth_m - rackWidth_m) / 2
    zoneX_px = Math.round((options.startX_m + rackWidth_m + delta_m) * PPM)
    zoneY_px = Math.round((options.startY_m + delta_m) * PPM)
    zoneW_px = aisleWidth_px
    zoneH_px = rowLength_px
  }

  const aisleZone: GeneratedAisleZone = {
    id: genId(),
    x: zoneX_px,
    y: zoneY_px,
    w: zoneW_px,
    h: zoneH_px,
    aisleType: finalAisleType,
    label: finalAisleType === 'containment'
      ? `Изолированный коридор ${options.rowName}/${pairedRowName}`
      : finalAisleType === 'cold'
      ? `Холодный коридор ${options.rowName}/${pairedRowName}`
      : `Горячий коридор ${options.rowName}/${pairedRowName}`,
    isDirty: true,
    isContained,
  }

  return {
    primaryRow,
    primaryRacks,
    pairedRow: pairedResult.row,
    pairedRacks: pairedResult.racks,
    aisleZone,
  }
}
